import { DayOfWeek, daysOfWeekValues, defaultWorkingDays, StringHHmm, StringYYYYMMDD, StringYYYYMMDDHHmm } from '../models/date-time';
import { ExpertSalonKey, Timetable } from '../models/timetable';
import { toStringHHmm, toStringYYYYMMDD } from './date-type-functions';
import { PublicTimetable } from '../models/public-timetable';
import { DateSlotParts } from '../models/date-slot-parts';
import { SlotUtils } from './slot-utils';
import { TimetableCore } from '../models/timetable-core';
import { DailySchedule, DailyScheduleMap } from '../models/daily-schedule';
import { DateUtils } from './date-utils';

export interface DateInfo {
  date: Date;
  dayOfWeek: DayOfWeek;
  dayOfWeekIndex: number;
}

export class TimetablesUtils {
  public static assembleExpertSalon(expertId: string, salonId: string): ExpertSalonKey {
    if (!expertId || !salonId) {
      throw new Error('Invalid expertId or salonId');
    }
    return `${expertId}:${salonId}` as ExpertSalonKey;
  }

  public static parseDateString(stringYYYYMMDD: StringYYYYMMDD): DateInfo | null {
    if (!stringYYYYMMDD || stringYYYYMMDD.length !== 8) {
      return null;
    }
    const year = parseInt(stringYYYYMMDD.substring(0, 4), 10);
    const month = parseInt(stringYYYYMMDD.substring(4, 6), 10);
    const day = parseInt(stringYYYYMMDD.substring(6, 8), 10);
    const date = new Date(year, month - 1, day);
    const dayOfWeekIndex = (date.getDay() + 6) % 7;
    const dayOfWeek = daysOfWeekValues[dayOfWeekIndex];
    return { date, dayOfWeek, dayOfWeekIndex };
  }

  public static parseDateSlot(dateSlot: StringYYYYMMDDHHmm): DateSlotParts | null {
    if (!dateSlot || dateSlot.length !== 12) {
      return null;
    }
    return {
      date: toStringYYYYMMDD(dateSlot.substring(0, 8)),
      timeSlot: toStringHHmm(dateSlot.substring(8)),
    };
  }

  public static findApplicableTimetable(timetables: Timetable[], dateString: StringYYYYMMDD): Timetable | null;
  public static findApplicableTimetable(timetables: PublicTimetable[], dateString: StringYYYYMMDD): PublicTimetable | null;
  public static findApplicableTimetable(
    timetables: Timetable[] | PublicTimetable[],
    dateString: StringYYYYMMDD,
  ): Timetable | PublicTimetable | null {
    const validTimetables = timetables.filter((t) => t.validFrom <= dateString).sort((a, b) => b.validFrom.localeCompare(a.validFrom));
    return validTimetables.length > 0 ? validTimetables[0] : null;
  }

  // daily slots for 'YYYYMMDD'
  public static generateTimeSlotsForDate(timetableCore: TimetableCore, targetDate: StringYYYYMMDD): StringHHmm[] {
    if (!TimetablesUtils.isDateEligibleForSlots(targetDate, timetableCore)) {
      return [];
    }

    const dateInfo = TimetablesUtils.parseDateString(targetDate);
    const dailySchedule = dateInfo ? timetableCore.dailySchedules[dateInfo.dayOfWeek] : undefined;

    const baseSlots = dailySchedule
      ? SlotUtils.generateTimeSlots(dailySchedule.startTime, dailySchedule.endTime, dailySchedule.slotInterval, dailySchedule.excludedSlots)
      : [];

    const timeSlotsForDate = (dateSlots: StringYYYYMMDDHHmm[]): StringHHmm[] =>
      (dateSlots ?? [])
        .map((dateSlot: StringYYYYMMDDHHmm) => TimetablesUtils.parseDateSlot(dateSlot))
        .filter((parts: DateSlotParts | null): parts is DateSlotParts => parts !== null && parts.date === targetDate)
        .map((parts: DateSlotParts) => parts.timeSlot);

    const excludedDateSlotsForDate = timeSlotsForDate(timetableCore.excludedDateSlots);
    const includedDateSlotsForDate = timeSlotsForDate(timetableCore.includedDateSlots);
    const customIncludedDateSlotsForDate = timeSlotsForDate(timetableCore.customIncludedDateSlots);

    const filteredSlots = baseSlots.filter((slot: StringHHmm) => !excludedDateSlotsForDate.includes(slot));

    const allSlots = new Set([...filteredSlots, ...includedDateSlotsForDate, ...customIncludedDateSlotsForDate]);

    return Array.from(allSlots).sort();
  }

  public static isDateEligibleForSlots(date: StringYYYYMMDD, timetableCore: TimetableCore): boolean {
    const isExcludedDate = timetableCore.excludedDates?.includes(date) ?? false;
    if (isExcludedDate) {
      return false;
    }

    const dateInfo = TimetablesUtils.parseDateString(date);
    if (!dateInfo) {
      return false;
    }

    const isWorkingDay = timetableCore.workingDays.includes(dateInfo.dayOfWeek);
    const hasCustomIncludedSlots = (timetableCore.customIncludedDateSlots ?? []).some((dateSlot: StringYYYYMMDDHHmm) =>
      dateSlot.startsWith(date),
    );

    return isWorkingDay || hasCustomIncludedSlots;
  }

  public static toTimetableCore(timetable: Timetable | PublicTimetable): TimetableCore {
    if ('expertSalon' in timetable) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { expertSalon, ...core } = timetable;
      return core;
    } else if ('expertId' in timetable) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { expertId, ...core } = timetable;
      return core;
    } else {
      throw new Error('Invalid timetable');
    }
  }
  public static cleanupExcludedSlots(timetable: Timetable): Timetable {
    const excludedDatesSet = new Set(timetable.excludedDates ?? []);

    // Keep only working-day schedules, and within each drop regular excluded slots that fall outside that day's grid.
    const dailySchedules = Object.fromEntries(
      Object.entries(timetable.dailySchedules)
        .filter(
          (entry): entry is [string, DailySchedule] => entry[1] !== undefined && timetable.workingDays.includes(entry[0] as DayOfWeek),
        )
        .map(([day, schedule]) => {
          const slots = SlotUtils.generateTimeSlots(schedule.startTime, schedule.endTime, schedule.slotInterval);
          const excludedSlots = (schedule.excludedSlots ?? []).filter((slot: StringHHmm) => slots.includes(slot));
          return [day, { ...schedule, excludedSlots }];
        }),
    ) as DailyScheduleMap;

    const gridForSchedule = (schedule: DailySchedule): StringHHmm[] =>
      SlotUtils.generateTimeSlots(schedule.startTime, schedule.endTime, schedule.slotInterval);

    // Regular-slot deselections: keep only for non-excluded working dates whose slot is still an active regular slot.
    const excludedDateSlots = (timetable.excludedDateSlots ?? []).filter((dateSlot: StringYYYYMMDDHHmm) => {
      const parts = TimetablesUtils.parseDateSlot(dateSlot);
      const schedule = parts ? TimetablesUtils.scheduleForDate(dailySchedules, parts.date) : undefined;
      if (!parts || !schedule || excludedDatesSet.has(parts.date)) {
        return false;
      }
      return gridForSchedule(schedule).includes(parts.timeSlot) && !schedule.excludedSlots.includes(parts.timeSlot);
    });

    // Regular-slot re-inclusions: keep only where the slot exists in the grid and is excluded by the weekly schedule.
    const includedDateSlots = (timetable.includedDateSlots ?? []).filter((dateSlot: StringYYYYMMDDHHmm) => {
      const parts = TimetablesUtils.parseDateSlot(dateSlot);
      const schedule = parts ? TimetablesUtils.scheduleForDate(dailySchedules, parts.date) : undefined;
      if (!parts || !schedule || excludedDatesSet.has(parts.date)) {
        return false;
      }
      return gridForSchedule(schedule).includes(parts.timeSlot) && schedule.excludedSlots.includes(parts.timeSlot);
    });

    // Custom appointments: keep only slots that fall outside the regular grid for that date (incl. non-working days).
    const customIncludedDateSlots = (timetable.customIncludedDateSlots ?? []).filter((dateSlot: StringYYYYMMDDHHmm) => {
      const parts = TimetablesUtils.parseDateSlot(dateSlot);
      if (!parts) {
        return false;
      }
      const schedule = TimetablesUtils.scheduleForDate(dailySchedules, parts.date);
      return !schedule || !gridForSchedule(schedule).includes(parts.timeSlot);
    });

    return {
      ...timetable,
      dailySchedules,
      excludedDateSlots,
      includedDateSlots,
      customIncludedDateSlots,
    };
  }

  /** Keyless default timetable seed. Callers supply the identity key via {@link defaultTimetable}. */
  public static defaultTimetableCore(): TimetableCore {
    return {
      validFrom: DateUtils.dateNow(),
      workingDays: defaultWorkingDays,
      dailySchedules: TimetablesUtils.defaultDailySchedules(),
      excludedDateSlots: [],
      includedDateSlots: [],
      excludedDates: [],
      customIncludedDateSlots: [],
    };
  }

  public static defaultTimetable(expertSalonKey: ExpertSalonKey): Timetable {
    return { expertSalon: expertSalonKey, ...TimetablesUtils.defaultTimetableCore() };
  }

  /** Projects an admin {@link Timetable} onto its public, expert-scoped representation. */
  public static toPublicTimetable(timetable: Timetable, expertId: string): PublicTimetable {
    return { expertId, ...TimetablesUtils.toTimetableCore(timetable) };
  }

  public static defaultDailySchedules(): DailyScheduleMap {
    return Object.fromEntries(defaultWorkingDays.map((day) => [day, TimetablesUtils.defaultDailySchedule()])) as DailyScheduleMap;
  }

  public static defaultDailySchedule(): DailySchedule {
    return {
      startTime: toStringHHmm('0900'),
      endTime: toStringHHmm('1700'),
      slotInterval: 30,
      excludedSlots: [],
    };
  }

  /**
   * Canonical cross-field rule for a daily schedule: the end time must not precede the start time.
   * Shared so that server-side (zod) and client-side (signal forms) validation cannot drift apart.
   * Operates on 'HHmm' strings, which compare lexicographically in chronological order.
   */
  public static isValidDailyScheduleRange(startTime: string, endTime: string): boolean {
    return startTime <= endTime;
  }

  public static flattenDailySchedule(dailySchedule: DailySchedule): string {
    const sortedExcludedSlots = [...dailySchedule.excludedSlots].sort();
    return [dailySchedule.startTime, dailySchedule.endTime, dailySchedule.slotInterval, ...sortedExcludedSlots].join('|');
  }

  public static flattenDailySchedules(dailySchedules: DailyScheduleMap): string {
    return Object.entries(dailySchedules)
      .filter((entry): entry is [string, DailySchedule] => entry[1] !== undefined)
      .sort(([dayA], [dayB]) => dayA.localeCompare(dayB))
      .map(([day, dailySchedule]) => `${day}:${TimetablesUtils.flattenDailySchedule(dailySchedule)}`)
      .join(';');
  }

  public static removeStaleObjects(timetable: Timetable): Timetable {
    const dailySchedules = Object.fromEntries(
      Object.entries(timetable.dailySchedules).filter(
        (entry): entry is [string, DailySchedule] => entry[1] !== undefined && timetable.workingDays.includes(entry[0] as DayOfWeek),
      ),
    ) as DailyScheduleMap;
    const excludedDates = timetable.excludedDates ? TimetablesUtils.normalizeYYYYMMDDSlots(timetable.excludedDates) : [];
    const excludedDateSlots = timetable.excludedDateSlots
      ? TimetablesUtils.normalizeYYYYMMDDHHmmSlots(timetable.excludedDateSlots, excludedDates)
      : [];
    const includedDateSlots = timetable.includedDateSlots
      ? TimetablesUtils.normalizeYYYYMMDDHHmmSlots(timetable.includedDateSlots, excludedDates)
      : [];
    const customIncludedDateSlots = timetable.customIncludedDateSlots
      ? TimetablesUtils.normalizeYYYYMMDDHHmmSlots(timetable.customIncludedDateSlots)
      : [];

    return { ...timetable, dailySchedules, excludedDateSlots, includedDateSlots, excludedDates, customIncludedDateSlots };
  }

  public static normalizeYYYYMMDDSlots(slots: StringYYYYMMDD[]): StringYYYYMMDD[] {
    return [...new Set(slots)].filter((slot: StringYYYYMMDD) => !DateUtils.isDateOlderThanYesterday(slot)).sort();
  }

  public static normalizeYYYYMMDDHHmmSlots(slots: StringYYYYMMDDHHmm[], excludedDates: StringYYYYMMDD[] = []): StringYYYYMMDDHHmm[] {
    const excludedDatesSet = new Set(excludedDates);
    return [...new Set(slots)]
      .filter((slot: StringYYYYMMDDHHmm) => {
        const datePart = DateUtils.getDatePartYYYYMMDD(slot);
        return !DateUtils.isDateOlderThanYesterday(datePart) && !excludedDatesSet.has(datePart);
      })
      .sort();
  }

  private static scheduleForDate(dailySchedules: DailyScheduleMap, date: StringYYYYMMDD): DailySchedule | undefined {
    const dateInfo = TimetablesUtils.parseDateString(date);
    return dateInfo ? dailySchedules[dateInfo.dayOfWeek] : undefined;
  }
}
